/**
 * MissionSocket — one WebSocket per open mission.
 *
 * - Connects to `/api/ws?missionId=…` (cookie auth; same origin via the Vite proxy in dev).
 * - Clock sync: a burst of 5 pings at connect, then one every 5 s; each pong feeds `clock`.
 * - Reconnects with exponential backoff + full jitter (0.5 s → 30 s), immediately when the
 *   browser comes back online or the tab becomes visible.
 * - Watchdog: if nothing arrives for 15 s the socket is presumed dead and recycled.
 * - After every (re)connect, calls `onReconnect(lastSeenT)` so the store can backfill via
 *   `GET telemetry/since`.
 */
import type { Callout, Id, ServerMessage, TrafficTarget } from '@apos/shared';
import { clientNow, clock } from './clock';
import { WS_CLOSE_ACCOUNT_SUSPENDED } from '@apos/shared';
import { signalSuspended, signalUnauthorized } from './api';

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline' | 'closed';

export interface MissionSocketOptions {
  missionId: Id;
  onMessage: (msg: ServerMessage) => void;
  onStatus?: (status: SocketStatus, info: { attempt: number; nextRetryAt: number | null }) => void;
  /** Called after each successful open except the first; the store backfills from here. */
  onReconnect?: () => void;
  /** Override for tests / native. */
  url?: string;
  WebSocketImpl?: typeof WebSocket;
}

type TrafficListener = (missionId: Id, targets: TrafficTarget[]) => void;
const trafficListeners = new Set<TrafficListener>();
/**
 * Other-traffic pictures (`{type:'traffic'}`) go to these listeners (state/traffic.ts) instead of
 * the mission record: they are map context, not mission telemetry.
 */
export function onTrafficMessage(fn: TrafficListener): () => void {
  trafficListeners.add(fn);
  return () => trafficListeners.delete(fn);
}

type CalloutListener = (callout: Callout) => void;
const calloutListeners = new Set<CalloutListener>();
/** Spoken updates for this user (`{type:'callout'}`) go to these listeners (state/callouts.ts). */
export function onCalloutMessage(fn: CalloutListener): () => void {
  calloutListeners.add(fn);
  return () => calloutListeners.delete(fn);
}

export const PING_INTERVAL_MS = 5000;
export const WATCHDOG_MS = 15_000;
const BURST = [0, 120, 260, 420, 600];

/** Full-jitter exponential backoff. attempt 0 → ≤0.5 s … capped at 30 s. */
export function backoffDelay(attempt: number, rand: () => number = Math.random): number {
  const cap = Math.min(30_000, 500 * 2 ** Math.min(attempt, 10));
  return Math.round(250 + rand() * (cap - 250));
}

export function missionSocketUrl(missionId: Id): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/ws?missionId=${encodeURIComponent(missionId)}`;
}

export class MissionSocket {
  private ws: WebSocket | null = null;
  private status: SocketStatus = 'idle';
  private attempt = 0;
  private opened = 0;
  private stopped = true;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private burstTimers: ReturnType<typeof setTimeout>[] = [];
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastRx = 0;
  private nextRetryAt: number | null = null;
  /** Map of outstanding ping send-times keyed by the clientTime we sent. */
  private pending = new Map<number, number>();

  constructor(private readonly opts: MissionSocketOptions) {}

  get currentStatus(): SocketStatus {
    return this.status;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibility);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setStatus('offline');
      return;
    }
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.clearTimers();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close(1000, 'client stop');
      } catch {
        /* ignore */
      }
    }
    this.setStatus('closed');
  }

  /** Force an immediate reconnect attempt (e.g. user taps the connection indicator). */
  reconnectNow(): void {
    if (this.stopped) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.attempt = 0;
    this.teardownSocket();
    this.connect();
  }

  private connect(): void {
    if (this.stopped) return;
    this.teardownSocket();
    this.setStatus(this.opened > 0 || this.attempt > 0 ? 'reconnecting' : 'connecting');
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    let ws: WebSocket;
    try {
      ws = new Impl(this.opts.url ?? missionSocketUrl(this.opts.missionId));
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const wasReconnect = this.opened > 0;
      this.opened++;
      this.attempt = 0;
      this.nextRetryAt = null;
      this.lastRx = clientNow();
      this.setStatus('open');
      this.startPinging();
      if (wasReconnect) this.opts.onReconnect?.();
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.lastRx = clientNow();
      let msg: ServerMessage;
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'pong') {
        const t0 = this.pending.get(msg.clientTime) ?? msg.clientTime;
        this.pending.delete(msg.clientTime);
        clock.addSample(t0, msg.serverTime, clientNow());
      } else if (msg.type === 'hello') {
        clock.hint(msg.serverTime);
      } else if (msg.type === 'traffic') {
        const targets = Array.isArray(msg.data) ? msg.data : [];
        for (const l of trafficListeners) l(this.opts.missionId, targets);
        return;
      } else if (msg.type === 'callout') {
        if (msg.data && typeof msg.data.text === 'string') for (const l of calloutListeners) l(msg.data);
        return;
      }
      this.opts.onMessage(msg);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      if (this.stopped) return;
      // 4404: mission deleted — nothing to reconnect to.
      if (ev.code === 4404) {
        this.stopped = true;
        this.setStatus('closed');
        return;
      }
      // 4401: session ended (logged out / expired) — stop and send the user to sign in.
      if (ev.code === 4401) {
        this.stopped = true;
        this.setStatus('closed');
        signalUnauthorized();
        return;
      }
      // 4423: account suspended — stop; the session layer fetches the notice and shows the suspension screen.
      if (ev.code === WS_CLOSE_ACCOUNT_SUSPENDED) {
        this.stopped = true;
        this.setStatus('closed');
        signalSuspended();
        return;
      }
      // 4403: membership refused — don't hammer.
      if (ev.code === 4403 || ev.code === 1008) {
        this.attempt = Math.max(this.attempt, 6);
      }
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setStatus('offline');
      return;
    }
    const delay = backoffDelay(this.attempt++);
    this.nextRetryAt = Date.now() + delay;
    this.setStatus('reconnecting');
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private startPinging(): void {
    this.clearTimers();
    for (const d of BURST) this.burstTimers.push(setTimeout(() => this.ping(), d));
    this.pingTimer = setInterval(() => this.ping(), PING_INTERVAL_MS);
    this.watchdog = setInterval(() => {
      if (clientNow() - this.lastRx > WATCHDOG_MS) {
        // dead socket (half-open TCP, sleeping radio): recycle
        this.teardownSocket();
        this.scheduleRetry();
      }
    }, 2500);
  }

  private ping(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== 1) return;
    // clientTime is what the server echoes; we key on it to recover our precise send time.
    const t0 = clientNow();
    const clientTime = Math.round(t0);
    this.pending.set(clientTime, t0);
    if (this.pending.size > 32) {
      const first = this.pending.keys().next().value;
      if (first !== undefined) this.pending.delete(first);
    }
    try {
      ws.send(JSON.stringify({ type: 'ping', clientTime }));
    } catch {
      /* close will follow */
    }
  }

  private teardownSocket(): void {
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  }

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.watchdog) clearInterval(this.watchdog);
    for (const t of this.burstTimers) clearTimeout(t);
    this.pingTimer = null;
    this.watchdog = null;
    this.burstTimers = [];
  }

  private setStatus(s: SocketStatus): void {
    if (this.status === s && s !== 'reconnecting') return;
    this.status = s;
    this.opts.onStatus?.(s, { attempt: this.attempt, nextRetryAt: this.nextRetryAt });
  }

  private handleOnline = () => {
    if (this.stopped) return;
    this.attempt = 0;
    if (!this.ws) this.connect();
  };

  private handleOffline = () => {
    if (this.stopped) return;
    this.teardownSocket();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.setStatus('offline');
  };

  private handleVisibility = () => {
    if (this.stopped || document.visibilityState !== 'visible') return;
    if (!this.ws && navigator.onLine !== false) {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.attempt = 0;
      this.connect();
    } else if (this.ws && clientNow() - this.lastRx > WATCHDOG_MS) {
      this.reconnectNow();
    } else {
      this.ping();
    }
  };
}

